import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import api from '../../../services/api';
import { OPTIONS } from './mvData';

const DICTS = {
  mood: 'audioMood',
  language: 'music_languare',
  style: 'audioGenre',
  vocal: 'audioVocalType',
  duration: 'audioDuration',
  imageMode: 'imageAudioMode',
};

export default function useMvDictionaries() {
  const { language } = useI18n();
  const [remote, setRemote] = useState({});
  const [loading, setLoading] = useState(false);
  const apiLanguage = language === 'en' ? 'en' : language === 'zh-TW' ? 'tc' : 'sc';

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const response = await api.get('/dictDetails', {
          params: {
            dictNames: Object.values(DICTS).join(','),
            language: apiLanguage,
            page: 0,
            limit: 100,
          },
        });
        if (active) setRemote(normalizeDicts(response.data || {}));
      } catch (error) {
        if (active) setRemote({});
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [apiLanguage]);

  const options = useMemo(() => ({
    mood: remote.audioMood || OPTIONS.mood,
    language: remote.music_languare || OPTIONS.language,
    style: remote.audioGenre || OPTIONS.style,
    vocal: remote.audioVocalType || OPTIONS.vocal,
    duration: remote.audioDuration || OPTIONS.duration,
    imageMode: remote.imageAudioMode || OPTIONS.imageMode,
  }), [remote]);

  return { options, loading };
}

function normalizeDicts(body) {
  const result = {};
  Object.keys(body).forEach((name) => {
    const list = Array.isArray(body[name]?.data) ? body[name].data : [];
    const options = normalizeOptions(name, list);
    if (options.length) result[name] = options;
  });
  return result;
}

function normalizeOptions(dictName, list) {
  const seen = new Set();
  return list
    .filter((item) => item?.value && belongsToDict(item, dictName))
    .sort((a, b) => toSort(a) - toSort(b))
    .map(toOption)
    .filter((option) => {
      const key = optionKey(option);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function toOption(item) {
  const value = String(item.value || '').trim();
  const label = String(item.label || value).trim();
  return {
    value,
    label,
    description: item.description || '',
  };
}

function belongsToDict(item, dictName) {
  const name = item.dictName || item.dict?.name || item.dict?.dictName || '';
  return !name || name === dictName;
}

function optionKey(option) {
  const value = option.value.trim().toLowerCase();
  const label = option.label.trim().toLowerCase();
  if (['auto', '自动', '自動'].includes(value) || ['auto', '自动', '自動'].includes(label)) {
    return 'auto';
  }
  return value;
}

function toSort(item) {
  return typeof item.sort === 'number' ? item.sort : parseInt(item.sort, 10) || 0;
}
